/**
 * 予約確認画面(Phase「LIFF frontend実装」)。
 *
 * 正本(実装指示書 v2.0 12章「予約確認」):
 * 現在のLINEユーザーに紐づく未来予約のみ表示。
 * 表示：日時、利用時間、金額、booking_id、支払状態、変更・無料キャンセル期限。
 * ボタン：予約変更申請、キャンセル申請、領収書を見る。
 *
 * 追加(Phase 9残件対応「配布導線」で既存APIに実装済みのため、正本の
 * 「返金明細への導線（現行APIに存在する場合）」指示に基づき対応):
 * refund_statement_pdf_urlが存在する予約(bookings/cancelled_refunds
 * いずれも)には「返金明細を見る」ボタンを追加する。
 *
 * 領収書について(Phase 11 Step 11-1「領収書PDF BLOCKER解消」で実装):
 * GET /v1/bookings/mineのレスポンスのreceipt_pdf_urlが設定されている場合
 * のみ「領収書を見る」ボタンを表示する。決済確定直後はPDF生成が
 * ベストエフォートで進行中のため、まだreceipt_pdf_urlが無い間は
 * 「準備中」の非活性表示のままにし、存在しない実体をAPI呼び出しで
 * 取得しようとしない(返金明細と同じ設計)。
 *
 * 利用履歴→領収書導線(2026-09-25 PR #5指示、2026-09-25レビュー修正で
 * 一覧掲載条件を変更): 「予約を確認する」を予約情報の入口として維持しつつ、
 * その中に「利用履歴（過去の予約）」を設ける。独立した領収書一覧は作らず、
 * `予約を確認する → 利用履歴 → 対象予約の詳細 → 領収書を見る`という
 * 導線にする。
 *
 * 利用履歴の対象データ(レビュー修正で変更): 認証済み本人の
 * reservation_status='利用完了'の予約のみ(GET /v1/bookings/mineの
 * usage_history、findUsageHistoryForUser)。「過去日＋決済済」という
 * 当初の代理条件(旧receipt_history)は、単に過去日／決済済だけでは
 * 利用履歴に含めないというレビュー指摘により廃止した。キャンセル済みは
 * ここに含めず、既存のcancelled_refunds側で扱う。
 *
 * 利用履歴への掲載と領収書発行可否は別の関心事として分離している。
 * 領収書ボタンの表示条件は、現在・今後の予約カード(bookingCardHtml)と
 * 同じ`payment_status === "決済済"`を見る(利用完了予約のうち、既存の
 * 領収書発行条件を満たす場合だけ表示する。実際のfail-closed境界は
 * receipt-snapshot-service.tsのサーバー側判定)。
 */

import type { AppContext } from "../app-context.js";
import { ApiError } from "../api-client.js";
import type {
  CancelledRefundStatementSummary,
  FutureBookingSummary,
  ReceiptSnapshot,
  ReceiptView,
  UsageHistorySummary,
} from "../types.js";
import receiptLogoUrl from "../../../assets/brand/tasuie-official-logo.png";
import { escapeHtml } from "../dom-utils.js";
import { renderCalendarWidget } from "../calendar-widget.js";
import {
  WEEKDAY_START_TIME_OPTIONS,
  WEEKEND_HOLIDAY_START_TIME,
  WEEKEND_HOLIDAY_DURATION_HOURS,
  durationOptionsForStartTime,
  isWeekendOrHolidayDate,
  computeEndTime,
  bookableDateRange,
  isBookableDate,
  ARRIVAL_TIME_OPTIONS,
} from "../booking-form-options.js";
import { estimatePriceYen } from "../pricing-estimate.js";

function formatYen(amount: number): string {
  return `${amount.toLocaleString("ja-JP")}円`;
}

function paymentStatusBadge(status: FutureBookingSummary["payment_status"]): string {
  const cls = status === "決済済" ? "badge" : status === "決済失敗" ? "badge disabled" : "badge pending";
  return `<span class="${cls}">${escapeHtml(status)}</span>`;
}

/**
 * 来館時刻の表示。土日祝は予約時に選んだ来館時刻(未設定の既存予約は「未設定」と
 * 明示し、推測で補完しない)、平日は予約の開始時刻がそのまま来館時刻。
 */
function arrivalTimeHtml(booking: FutureBookingSummary): string {
  if (booking.day_type === "weekend_holiday") {
    return booking.arrival_time
      ? `<p>来館時刻: <strong>${escapeHtml(booking.arrival_time)}</strong>(スタッフが解錠・施設のご案内を行います)</p>`
      : `<p>来館時刻: 未設定(「予約変更を申請」から設定できます)</p>`;
  }
  return `<p>来館時刻: <strong>${escapeHtml(booking.start_time)}</strong>(ご予約の開始時刻にスタッフが伺い、解錠・施設のご案内を行います)</p>`;
}

function bookingCardHtml(booking: FutureBookingSummary): string {
  const deadline = booking.change_free_cancel_deadline
    ? new Date(booking.change_free_cancel_deadline).toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" })
    : "―";
  const refundLink = booking.refund_statement_pdf_url
    ? `<button type="button" class="secondary" data-action="refund-statement" data-url="${escapeHtml(booking.refund_statement_pdf_url)}">返金明細を見る</button>`
    : "";
  const receiptSection = booking.payment_status === "決済済"
    ? `<p><button type="button" class="secondary" data-action="receipt" data-booking-id="${escapeHtml(booking.booking_id)}">領収書を見る</button></p>`
    : `<p>領収書: <span class="badge disabled">準備中</span></p>`;
  const canOperate = booking.reservation_status === "予約確定" || booking.reservation_status === "確定処理中";

  return `
    <div class="card" data-booking-id="${escapeHtml(booking.booking_id)}">
      <p><strong>${escapeHtml(booking.use_date)} ${escapeHtml(booking.start_time)}〜${escapeHtml(booking.end_time)}</strong></p>
      ${arrivalTimeHtml(booking)}
      <p>予約ID: ${escapeHtml(booking.booking_id)}</p>
      <p>金額: ${formatYen(booking.booking_amount)} ${paymentStatusBadge(booking.payment_status)}</p>
      <p>予約状態: ${escapeHtml(booking.reservation_status)}</p>
      <p>変更・無料キャンセル期限: ${escapeHtml(deadline)}</p>
      ${receiptSection}
      ${refundLink}
      ${
        // iPhone実機確認で発見・是正(Phase 11 Part H): 予約変更ボタンに
        // 従来の`.secondary`(淡い灰色)を使うと`:disabled`と見分けがつかず
        // 「押せなさそう」に見えてしまうため専用の`.change-action`
        // (アクセントカラーの輪郭線)を使う。キャンセルは引き続き赤系だが
        // 塗りつぶしではなく輪郭線にして、変更ボタンより過度に強く見えない
        // よう相対的な視覚的重みを揃える(style.css参照)。無料変更・
        // キャンセル期限を過ぎても「変更申請」自体を拒否する業務ルールは
        // 現行バックエンドに存在しない(期限超過は差額・手数料の扱いが
        // 変わるだけ)ため、根拠のない disabled 状態を作らない。
        canOperate
          ? `<div class="card-actions">
               ${
                 booking.day_type === "weekend_holiday"
                   ? `<button type="button" class="change-action" data-action="change-arrival" data-booking-id="${escapeHtml(booking.booking_id)}">来館時刻を変更</button>`
                   : ""
               }
               <button type="button" class="change-action" data-action="change" data-booking-id="${escapeHtml(booking.booking_id)}">日時の変更を申請</button>
               <button type="button" class="danger" data-action="cancel" data-booking-id="${escapeHtml(booking.booking_id)}">キャンセルする</button>
             </div>`
          : ""
      }
    </div>
  `;
}

function cancelledRefundCardHtml(item: CancelledRefundStatementSummary): string {
  const refundLink = item.refund_statement_pdf_url
    ? `<button type="button" class="secondary" data-action="refund-statement" data-url="${escapeHtml(item.refund_statement_pdf_url)}">返金明細を見る</button>`
    : `<p class="field-hint">返金明細を準備中です。しばらくしてから再度お試しください。</p>`;

  return `
    <div class="card" data-booking-id="${escapeHtml(item.booking_id)}">
      <p><strong>${escapeHtml(item.use_date)} ${escapeHtml(item.start_time)}〜${escapeHtml(item.end_time)}</strong>(キャンセル済・返金済)</p>
      <p>予約ID: ${escapeHtml(item.booking_id)}</p>
      <p>返金額: ${formatYen(item.refund_amount)}</p>
      ${refundLink}
    </div>
  `;
}

function usageHistoryCardHtml(item: UsageHistorySummary): string {
  return `<div class="card" data-booking-id="${escapeHtml(item.booking_id)}">
    <p><strong>${escapeHtml(item.use_date)} ${escapeHtml(item.start_time)}〜${escapeHtml(item.end_time)}</strong></p>
    <p>金額: ${formatYen(item.booking_amount)}</p>
    <button type="button" class="secondary" data-action="usage-history-detail" data-booking-id="${escapeHtml(item.booking_id)}">予約の詳細を見る</button>
    <button type="button" class="secondary" data-action="usage-history-hide" data-booking-id="${escapeHtml(item.booking_id)}">この履歴を削除</button>
  </div>`;
}

/**
 * 利用履歴の各予約詳細。「対象予約の詳細 → 領収書を見る」の入口。
 * 領収書ボタンは、現在・今後の予約カード(bookingCardHtml)と同じ
 * `payment_status === "決済済"`のときだけ表示する(新しい判定ルールでは
 * なく既存条件の再利用。実際のアクセス可否はreceipt-snapshot-service.ts
 * のサーバー側判定がfail closedで担保する)。
 */
function usageHistoryDetailHtml(item: UsageHistorySummary): string {
  const receiptSection = item.payment_status === "決済済"
    ? `<p><button type="button" data-action="receipt" data-booking-id="${escapeHtml(item.booking_id)}">領収書を見る</button></p>`
    : `<p>領収書: <span class="badge disabled">対象外</span></p>`;

  return `
    <section class="card" data-usage-history-detail="${escapeHtml(item.booking_id)}">
      <h3>ご利用履歴の詳細</h3>
      <p><strong>${escapeHtml(item.use_date)} ${escapeHtml(item.start_time)}〜${escapeHtml(item.end_time)}</strong></p>
      <p>予約ID: ${escapeHtml(item.booking_id)}</p>
      <p>金額: ${formatYen(item.booking_amount)} ${paymentStatusBadge(item.payment_status)}</p>
      <p>予約状態: ${escapeHtml(item.reservation_status)}</p>
      ${receiptSection}
      <p><button type="button" class="secondary" data-action="usage-history-back">利用履歴へ戻る</button></p>
    </section>
  `;
}

function renderUsageHistoryDetail(ctx: AppContext, item: UsageHistorySummary, root: HTMLElement): void {
  root.innerHTML = usageHistoryDetailHtml(item);
  root.querySelector<HTMLButtonElement>('[data-action="receipt"]')?.addEventListener("click", () => {
    void showReceipt(ctx, item.booking_id, root);
  });
  root.querySelector<HTMLButtonElement>('[data-action="usage-history-back"]')?.addEventListener("click", () => {
    void renderMyBookingsScreen(root, ctx);
  });
}

/**
 * 利用履歴の削除(本人の表示からの非表示)。予約・決済・領収書の記録自体は
 * 削除されず、一度非表示にした履歴は再表示されない。
 */
async function handleHideUsageHistory(ctx: AppContext, bookingId: string, root: HTMLElement): Promise<void> {
  const confirmed = window.confirm(
    "この利用履歴を一覧から削除します。削除した履歴は再表示できません(ご予約・お支払い・領収書の記録自体は削除されません)。よろしいですか?",
  );
  if (!confirmed) return;

  try {
    await ctx.api.hideUsageHistory(bookingId);
    await renderMyBookingsScreen(root, ctx);
    renderInlineMessage(root, "利用履歴を削除しました。", "info");
  } catch (error) {
    const reason = error instanceof ApiError ? error.reason ?? error.code : "unknown_error";
    renderInlineMessage(root, `履歴の削除に失敗しました(${reason})。時間をおいて再度お試しください。`, "error");
  }
}

async function downloadRefundStatement(ctx: AppContext, url: string, root: HTMLElement): Promise<void> {
  try {
    const blob = await ctx.api.fetchRefundStatementPdf(url);
    const objectUrl = URL.createObjectURL(blob);
    // LINE内ブラウザでも新しいタブ/ビューアで開けるよう window.open を使う。
    // 生のURL(署名なし・認証なし)をUIへ埋め込まず、認証済みfetchで取得した
    // Blobからobject URLを生成する点が重要(トークンをURLへ含めない)。
    window.open(objectUrl, "_blank");
    // object URLはページ側で明示的に破棄しない限りメモリに残るため、
    // 表示時間を確保した上で解放する。
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    const message =
      error instanceof ApiError && error.status === 409
        ? "返金明細はまだ準備中です。しばらくしてから再度お試しください。"
        : "返金明細の取得に失敗しました。時間をおいて再度お試しください。";
    renderInlineMessage(root, message, "error");
  }
}

function formatReceiptDate(iso: string): string {
  return new Intl.DateTimeFormat("ja-JP", { timeZone: "Asia/Tokyo", year: "numeric", month: "long", day: "numeric" }).format(new Date(iso));
}

function renderReceiptHtml(view: ReceiptView, root: HTMLElement, ctx: AppContext): void {
  const snapshot: ReceiptSnapshot = view.receipt;
  const issuerLines = [
    snapshot.issuer.company ? `<strong>${escapeHtml(snapshot.issuer.company)}</strong>` : "",
    snapshot.issuer.name ? escapeHtml(snapshot.issuer.name) : "",
    snapshot.issuer.address ? escapeHtml(snapshot.issuer.address) : "",
    snapshot.issuer.phone ? `TEL：${escapeHtml(snapshot.issuer.phone)}` : "",
    snapshot.issuer.registrationNumber ? `登録番号：${escapeHtml(snapshot.issuer.registrationNumber)}` : "",
  ].filter(Boolean).join("<br>");
  // 領収書カード(デザイン)の外側に表示する補足。再発行待ちの案内と、無効になった旧領収書の履歴。
  const pendingNotice = view.reissue_pending
    ? '<p class="field-hint receipt-reissue-pending">予約変更に伴う返金の完了後、変更後の金額で領収書を再発行します。完了までは下記の領収書が有効です。</p>'
    : "";
  const superseded = view.superseded ?? [];
  const supersededHtml = superseded.length
    ? `<section class="receipt-superseded"><h3>無効（再発行済み）の領収書</h3><ul>${superseded
        .map(
          (old) =>
            `<li>領収書番号 ${escapeHtml(old.receipt_number)}｜${formatYen(old.confirmed_amount)}（税込）｜発行日 ${escapeHtml(formatReceiptDate(old.issued_at))}｜無効</li>`,
        )
        .join("")}</ul></section>`
    : "";
  root.innerHTML = `${pendingNotice}
    <section class="receipt-view" data-receipt-booking-id="${escapeHtml(snapshot.bookingId)}">
      <div class="receipt-heading">
        <div class="receipt-logo"><img src="${escapeHtml(receiptLogoUrl)}" alt="タスイエ +House" width="220"></div>
        <div class="receipt-heading-meta"><h2>領収書</h2><small class="receipt-number">領収書番号 ${escapeHtml(snapshot.receiptNumber)}</small><small>発行日 ${escapeHtml(formatReceiptDate(snapshot.issuedAt))}</small></div>
      </div>
      <h3>${escapeHtml(snapshot.recipientName)} 様</h3>
      <p>下記の金額を正に領収いたしました。</p>
      <p class="receipt-total"><span>領収金額</span> ${formatYen(snapshot.confirmedAmount)}（税込）</p>
      <h3>ご利用内容</h3>
      <dl class="receipt-details">
        <dt>利用内容</dt><dd>${escapeHtml(snapshot.usageDescription)}</dd>
        <dt>ご利用料金</dt><dd>${formatYen(snapshot.confirmedAmount)}（税込）</dd>
        <dt>税抜金額（10%対象）</dt><dd>${formatYen(snapshot.taxableAmount)}</dd>
        <dt>消費税額（10%）</dt><dd>${formatYen(snapshot.taxAmount)}</dd>
        <dt>合計（税込）</dt><dd>${formatYen(snapshot.confirmedAmount)}</dd>
      </dl>
      ${issuerLines ? `<div class="receipt-issuer">${issuerLines}</div>` : ""}
      <button type="button" data-action="receipt-download">PDFを表示・保存</button>
      <button type="button" class="secondary" data-action="receipt-back">予約一覧へ戻る</button>
    </section>${supersededHtml}`;
  root.querySelector<HTMLButtonElement>('[data-action="receipt-download"]')?.addEventListener("click", async () => {
    await downloadReceipt(ctx, snapshot.bookingId, root);
  });
  root.querySelector<HTMLButtonElement>('[data-action="receipt-back"]')?.addEventListener("click", () => {
    void renderMyBookingsScreen(root, ctx);
  });
}

async function showReceipt(ctx: AppContext, bookingId: string, root: HTMLElement): Promise<void> {
  try {
    const response = await ctx.api.getReceiptView(bookingId);
    renderReceiptHtml(response, root, ctx);
  } catch (error) {
    const message =
      error instanceof ApiError && error.status === 409 && error.reason === "refund_pending"
        ? "予約変更に伴う返金の完了後に、変更後の金額で領収書を発行します。しばらくお待ちください。"
        : "領収書の表示に失敗しました。時間をおいて再度お試しください。";
    renderInlineMessage(root, message, "error");
  }
}

async function downloadReceipt(ctx: AppContext, bookingId: string, root: HTMLElement): Promise<void> {
  try {
    const ticket = await ctx.api.createReceiptDownloadTicket(bookingId);
    ctx.openExternal(ticket.download_url);
  } catch (error) {
    const message =
      error instanceof ApiError && error.status === 409
        ? "領収書はまだ準備中です。しばらくしてから再度お試しください。"
        : "領収書の取得に失敗しました。時間をおいて再度お試しください。";
    renderInlineMessage(root, message, "error");
  }
}

function renderInlineMessage(root: HTMLElement, message: string, kind: "error" | "info"): void {
  const el = document.createElement("p");
  el.className = kind;
  el.textContent = message;
  root.prepend(el);
  setTimeout(() => el.remove(), 6_000);
}

async function handleCancel(ctx: AppContext, bookingId: string, root: HTMLElement, booking?: FutureBookingSummary): Promise<void> {
  // 実際の支払い方法・入金状態に合った案内を出す(サーバー生成の文言。確定結果・LINE通知と同じ文言)。
  const policy = booking?.cancellation_policy;
  const confirmed = window.confirm(
    policy
      ? `予約をキャンセルします。\n・無料キャンセル期限内(利用日の2日前23:59まで): ${policy.free}\n・期限後(利用日の前日0:00以降): ${policy.full}\n(適用される区分は、次の画面に表示されます)\nよろしいですか?`
      // 案内文が無い(旧形式のAPI応答・支払い方法が不明)場合は、返金の有無を断定しない。
      // 料金区分(利用日の2日前23:59まで無料、前日0:00以降はキャンセル料100%)は全支払い方法に共通の正式ルール。
      : "予約をキャンセルします。\nキャンセル料の有無は、キャンセルの時期(利用日の2日前23:59までは無料、前日0:00以降は料金の100%)で決まります。\n返金の有無・方法は、お支払い方法とご入金状況によって異なります。確定後の画面とLINEでご案内します。\nよろしいですか?",
  );
  if (!confirmed) return;

  try {
    const result = await ctx.api.cancelBooking(bookingId);
    let message: string;
    if (result.customer_message) {
      // サーバーが生成した案内(支払い方法・入金状態・料金区分に合わせた、LINE通知と同じ文言)。
      message = `キャンセルが完了しました(${result.cancellation_fee_type === "free" ? "無料キャンセル" : "有料キャンセル"})。${result.customer_message}`;
    } else if (result.manual_refund_required) {
      // 現地払いで入金済み: Stripeの返金予定ではないため「全額返金」「返金額」を表示しない。
      message = "キャンセルが完了しました(無料キャンセル)。ご入金済みの金額の返金については、担当者から個別にご案内します。";
    } else {
      // 案内文が無い旧形式のAPI応答: 支払い方法を特定できないため、返金の有無を断定しない。
      // サーバーが記録した返金予定額(0円より大きい場合のみ)だけを事実として示す。
      const feeLabel = result.cancellation_fee_type === "free" ? "無料キャンセル" : "有料キャンセル";
      const refundNote = result.refund_amount > 0 ? `返金予定額: ${formatYen(result.refund_amount)}(返金手続きは追ってご案内します)。` : "";
      message = `キャンセルが完了しました(${feeLabel})。${refundNote}返金やご請求の詳細は、LINEのご案内をご確認ください。`;
    }
    // 一覧を再描画してから結果を表示する(先に表示すると再描画で消えて、結果が利用者に見えない)。
    await renderMyBookingsScreen(root, ctx);
    renderInlineMessage(root, message, "info");
  } catch (error) {
    const reason = error instanceof ApiError ? error.reason ?? error.code : "unknown_error";
    renderInlineMessage(root, `キャンセルに失敗しました(${reason})。時間をおいて再度お試しください。`, "error");
  }
}

/**
 * 来館時刻のみの変更(土日祝、管理者承認なし)。送信した時点で即時に確定し、
 * 料金・お支払い・予約枠は変わらない。日付を伴う変更は別フォーム(申請→承認)。
 */
function renderArrivalTimeForm(ctx: AppContext, booking: FutureBookingSummary, root: HTMLElement): void {
  root.querySelector("#arrival-change-form")?.remove();
  const container = document.createElement("div");
  container.className = "card";
  container.id = "arrival-change-form";
  container.innerHTML = `
    <h3>来館時刻を変更</h3>
    <p>${escapeHtml(booking.use_date)} ${escapeHtml(booking.start_time)}〜${escapeHtml(booking.end_time)}(1日貸切)</p>
    <div class="field">
      <label for="arrival-change-select">来館時刻</label>
      <select id="arrival-change-select">
        <option value="">選択してください</option>
        ${ARRIVAL_TIME_OPTIONS.map(
          (t) => `<option value="${t}" ${t === booking.arrival_time ? "selected" : ""}>${t}</option>`,
        ).join("")}
      </select>
      <p class="field-hint">来館時刻にスタッフが伺い、解錠・施設のご案内を行います(1時間刻み)。料金・お支払い・ご予約枠は変わらず、変更はすぐに反映されます。</p>
      <p id="arrival-change-error" class="error"></p>
    </div>
    <button type="button" data-action="submit-arrival-change">来館時刻を変更する</button>
    <button type="button" class="secondary" data-action="cancel-arrival-change">変更せず戻る</button>
  `;
  root.prepend(container);

  container.querySelector('[data-action="cancel-arrival-change"]')?.addEventListener("click", () => container.remove());
  container.querySelector('[data-action="submit-arrival-change"]')?.addEventListener("click", async () => {
    const errorEl = container.querySelector("#arrival-change-error") as HTMLElement;
    const value = (container.querySelector("#arrival-change-select") as HTMLSelectElement).value;
    if (!value) {
      errorEl.textContent = "来館時刻を選択してください。";
      return;
    }
    if (value === booking.arrival_time) {
      errorEl.textContent = "現在の来館時刻と同じです。";
      return;
    }
    const submitBtn = container.querySelector('[data-action="submit-arrival-change"]') as HTMLButtonElement;
    submitBtn.disabled = true;
    try {
      const result = await ctx.api.changeArrivalTime(booking.booking_id, { arrival_time: value });
      await renderMyBookingsScreen(root, ctx);
      renderInlineMessage(root, `来館時刻を${result.arrival_time}に変更しました。`, "info");
    } catch (error) {
      submitBtn.disabled = false;
      const reason = error instanceof ApiError ? error.reason ?? error.code : "unknown_error";
      if (reason === "change_request_already_active") {
        errorEl.textContent = "日付の変更申請の処理中は、来館時刻を変更できません。処理が完了してからお試しください。";
      } else if (reason === "arrival_time_conflict") {
        errorEl.textContent = "予約内容が更新されたため変更できませんでした。画面を開き直してもう一度お試しください。";
      } else if (reason === "invalid_booking_state") {
        errorEl.textContent = "この予約は来館時刻を変更できません。";
      } else {
        errorEl.textContent = `来館時刻の変更に失敗しました(${reason})。時間をおいて再度お試しください。`;
      }
    }
  });
}

interface ChangeFormState {
  use_date: string;
  start_time: string;
  duration: number;
  /** 土日祝の来館時刻(変更後)。平日は空。 */
  arrival_time: string;
}

/**
 * iPhone/LINE LIFF実機確認で発見・是正(Phase 11 Part F): 新規予約画面
 * (new-booking.ts)と全く同じ日時選択の不具合(`<input type="time"/"number">`
 * の端末依存自由入力)がここにも存在していたため、同じbooking-form-
 * options.tsの選択式候補を再利用する。新規予約と変更申請で入力制約が
 * 将来ズレないよう、候補値・判定ロジックは共通モジュールへ一本化した。
 */
function renderChangeRequestForm(ctx: AppContext, booking: FutureBookingSummary, root: HTMLElement): void {
  const bookingId = booking.booking_id;
  const container = document.createElement("div");
  container.className = "card";
  // 日付を伴う変更の申請(管理者承認あり)。来館時刻だけの変更は予約カードの
  // 「来館時刻を変更」から即時に行う(このフォームでは受け付けない)。
  // 平日の予約は、現在の利用日・開始/終了時刻を初期値にする(日付はそのままで開始・終了
  // 時刻だけを変えられる)。土日祝は1日貸しの固定枠のため、日付を選び直す変更のみ。
  const isWeekdayBooking = booking.day_type === "weekday" && isBookableDate(booking.use_date);
  const state: ChangeFormState = {
    use_date: isWeekdayBooking ? booking.use_date : "",
    start_time: isWeekdayBooking ? booking.start_time : "10:00",
    duration: isWeekdayBooking ? booking.duration : 1,
    arrival_time: "",
  };
  let fixedSlotShown = false; // 土日祝の1日貸し(10:00〜23:00固定)を表示中か
  const { min: minDate, max: maxDate } = bookableDateRange();
  const calendarState = (() => {
    const [y, m] = (state.use_date || minDate).split("-").map(Number);
    return { year: y ?? 2026, month: m ?? 1 };
  })();
  let unavailableDates = new Set<string>();
  let holidayDates = new Set<string>();
  let calendarCheckFailed = false;

  container.innerHTML = `
    <h3>日時の変更を申請</h3>
    <p class="field-hint">利用日・開始時刻・終了時刻を変更できます。管理者の承認後に確定します(承認されるまで元のご予約が有効です)。</p>
    <div class="field">
      <span id="change-use-date-label" class="field-label">利用日</span>
      <div id="change-calendar-container" aria-labelledby="change-use-date-label"></div>
      <p class="field-hint">当日のご予約はできません。ご利用日の前日までにお申し込みください(最大3か月先まで)。赤い×は既にご予約が入っている予約不可日です。</p>
      <p id="change-date-error" class="error"></p>
    </div>
    <div id="change-daytype-section"></div>
    <div id="change-summary" class="price-estimate"></div>
    <p class="field-hint">空き状況・差額の有無は送信後にサーバー側で確定します。</p>
    <button type="submit" data-action="submit-change">変更を申請する</button>
    <button type="button" class="secondary" data-action="cancel-change-form">変更せず戻る</button>
  `;
  root.prepend(container);

  const calendarContainer = container.querySelector("#change-calendar-container") as HTMLElement;

  const paintCalendar = () => {
    renderCalendarWidget(calendarContainer, {
      year: calendarState.year,
      month: calendarState.month,
      minDate,
      maxDate,
      selectedDate: state.use_date,
      unavailableDates,
      holidayDates,
      calendarCheckFailed,
      onSelectDate: (iso) => {
        state.use_date = iso;
        // 土日祝(1日貸し)から平日へ選び直した場合は、固定枠の値を引き継がず初期値へ戻す(Part 4)。
        if (fixedSlotShown) {
          state.start_time = "10:00";
          state.duration = 1;
        }
        (container.querySelector("#change-date-error") as HTMLElement).textContent = "";
        paintCalendar();
        renderDaytypeSection();
      },
      onMonthChange: (year, month) => {
        calendarState.year = year;
        calendarState.month = month;
        paintCalendar();
        void loadUnavailableDatesForVisibleMonth();
      },
    });
  };

  // 自分自身の予約(bookingId)は「他の予約」として自己ブロックしない
  // (backend availability-service.tsのexcludeBookingId、change-request-
  // service.tsの既存自己除外と同じ方針)。
  const loadUnavailableDatesForVisibleMonth = async () => {
    const pad = (n: number) => String(n).padStart(2, "0");
    const lastDay = new Date(Date.UTC(calendarState.year, calendarState.month, 0)).getUTCDate();
    const start = `${calendarState.year}-${pad(calendarState.month)}-01`;
    const end = `${calendarState.year}-${pad(calendarState.month)}-${pad(lastDay)}`;
    const fromDate = start < minDate ? minDate : start;
    const toDate = end > maxDate ? maxDate : end;
    if (fromDate > toDate) {
      unavailableDates = new Set();
      holidayDates = new Set();
      calendarCheckFailed = false;
      paintCalendar();
      return;
    }
    try {
      const result = await ctx.api.getUnavailableDates(fromDate, toDate, bookingId);
      unavailableDates = new Set(result.unavailable_dates);
      holidayDates = new Set(result.holiday_dates);
      calendarCheckFailed = result.calendar_check_ok === false;
    } catch {
      unavailableDates = new Set();
      holidayDates = new Set();
      calendarCheckFailed = true; // 取得できない場合は予約可能と判断しない
    }
    paintCalendar();
    renderDaytypeSection();
  };

  paintCalendar();
  void loadUnavailableDatesForVisibleMonth();

  const renderDaytypeSection = () => {
    const section = container.querySelector("#change-daytype-section") as HTMLElement;
    if (!state.use_date || !isBookableDate(state.use_date)) {
      section.innerHTML = "";
      renderSummary();
      return;
    }
    if (isWeekendOrHolidayDate(state.use_date, holidayDates)) {
      fixedSlotShown = true;
      state.start_time = WEEKEND_HOLIDAY_START_TIME;
      state.duration = WEEKEND_HOLIDAY_DURATION_HOURS;
      section.innerHTML = `
        <div class="field">
          <p class="fixed-slot"><strong>土日祝は1日貸し(10:00〜23:00 / 22,000円)です。</strong></p>
        </div>
        <div class="field">
          <label for="change-arrival-time">来館時刻(必須)</label>
          <select id="change-arrival-time">
            <option value="">選択してください</option>
            ${ARRIVAL_TIME_OPTIONS.map(
              (t) => `<option value="${t}" ${t === state.arrival_time ? "selected" : ""}>${t}</option>`,
            ).join("")}
          </select>
          <p class="field-hint">来館時刻にスタッフが解錠・施設のご案内を行います(1時間刻み)。来館時刻だけを変更する場合は、このフォームではなく予約カードの「来館時刻を変更」から行ってください(すぐに反映されます)。</p>
        </div>
      `;
      container.querySelector("#change-arrival-time")?.addEventListener("change", (e) => {
        state.arrival_time = (e.target as HTMLSelectElement).value;
        (container.querySelector("#change-date-error") as HTMLElement).textContent = "";
      });
      renderSummary();
      return;
    }
    fixedSlotShown = false;
    state.arrival_time = "";
    const durationOptions = durationOptionsForStartTime(state.start_time);
    if (!durationOptions.includes(state.duration)) {
      state.duration = durationOptions[0] ?? 1;
    }
    section.innerHTML = `
      <div class="field">
        <label for="change-start-time">開始時刻</label>
        <select id="change-start-time">
          ${WEEKDAY_START_TIME_OPTIONS.map(
            (t) => `<option value="${t}" ${t === state.start_time ? "selected" : ""}>${t}</option>`,
          ).join("")}
        </select>
      </div>
      <div class="field">
        <label for="change-end-time">終了時刻</label>
        <select id="change-end-time">
          ${durationOptions
            .map((d) => {
              const end = computeEndTime(state.start_time, d);
              return `<option value="${end}" ${d === state.duration ? "selected" : ""}>${end}</option>`;
            })
            .join("")}
        </select>
        <p class="field-hint">利用時間は1〜13時間(10時間以上は上限22,000円)。終了時刻は23:00までです。</p>
        <p class="field-hint">ご予約の開始時刻にスタッフが伺い、解錠・施設のご案内を行います。開始時刻にお越しください。</p>
      </div>
    `;
    container.querySelector("#change-start-time")?.addEventListener("change", (e) => {
      state.start_time = (e.target as HTMLSelectElement).value;
      renderDaytypeSection();
    });
    container.querySelector("#change-end-time")?.addEventListener("change", (e) => {
      const endHour = Number((e.target as HTMLSelectElement).value.split(":")[0]);
      const startHour = Number(state.start_time.split(":")[0]);
      state.duration = endHour - startHour;
      (container.querySelector("#change-date-error") as HTMLElement).textContent = "";
      renderSummary();
    });
    renderSummary();
  };

  /** 現在の予約と変更後の内容・概算料金・差額の目安を表示する(正式な金額は送信後にサーバーが確定)。 */
  const renderSummary = () => {
    const el = container.querySelector("#change-summary") as HTMLElement | null;
    if (!el) return;
    if (!state.use_date || !isBookableDate(state.use_date)) {
      el.textContent = "";
      return;
    }
    const newEnd = computeEndTime(state.start_time, state.duration);
    const newAmount = estimatePriceYen(state.use_date, state.duration);
    const diff = newAmount - booking.booking_amount;
    const diffLabel = diff > 0 ? `+${formatYen(diff)}` : diff < 0 ? `-${formatYen(Math.abs(diff))}` : "差額なし";
    el.innerHTML = `
      <p>現在: ${escapeHtml(booking.use_date)} ${escapeHtml(booking.start_time)}〜${escapeHtml(booking.end_time)}(${formatYen(booking.booking_amount)})</p>
      <p>変更後: ${escapeHtml(state.use_date)} ${escapeHtml(state.start_time)}〜${escapeHtml(newEnd)}(概算 ${formatYen(newAmount)})</p>
      <p>差額の目安: ${escapeHtml(diffLabel)}<span class="caption">概算です。正式な料金と、追加のお支払い・返金の扱いは承認時にサーバーが確定します</span></p>
    `;
  };

  const validateDate = (): boolean => {
    const errorEl = container.querySelector("#change-date-error") as HTMLElement;
    if (!state.use_date) {
      errorEl.textContent = "";
      return false;
    }
    if (!isBookableDate(state.use_date)) {
      errorEl.textContent = "選択された利用日には変更できません(当日・過去日・3か月より先の日付は選べません)。";
      return false;
    }
    errorEl.textContent = "";
    return true;
  };

  container.querySelector('[data-action="cancel-change-form"]')?.addEventListener("click", () => container.remove());

  container.querySelector('[data-action="submit-change"]')?.addEventListener("click", async () => {
    if (!state.use_date) {
      (container.querySelector("#change-date-error") as HTMLElement).textContent = "利用日を選択してください。";
      return;
    }
    if (!validateDate()) {
      return;
    }
    if (isWeekendOrHolidayDate(state.use_date, holidayDates) && !state.arrival_time) {
      (container.querySelector("#change-date-error") as HTMLElement).textContent = "来館時刻を選択してください。";
      return;
    }
    const endTime = computeEndTime(state.start_time, state.duration);
    if (
      booking.day_type === "weekday" &&
      state.use_date === booking.use_date &&
      state.start_time === booking.start_time &&
      endTime === booking.end_time
    ) {
      (container.querySelector("#change-date-error") as HTMLElement).textContent = "現在のご予約と同じ内容です。変更する項目を選んでください。";
      return;
    }

    try {
      const result = await ctx.api.submitChangeRequest(bookingId, {
        use_date: state.use_date,
        start_time: state.start_time,
        end_time: endTime,
        duration: state.duration,
        ...(state.arrival_time ? { arrival_time: state.arrival_time } : {}),
      });
      container.remove();
      renderInlineMessage(
        root,
        `変更申請を受け付けました(${result.price_change_type})。差額が必要な場合は「予約を確認する」画面から追加決済の案内が届きます。`,
        "info",
      );
    } catch (error) {
      const reason = error instanceof ApiError ? error.reason ?? error.code : "unknown_error";
      if (reason === "use_arrival_time_endpoint") {
        renderInlineMessage(root, "利用日が同じ場合は、予約カードの「来館時刻を変更」から変更してください。", "error");
        return;
      }
      if (reason === "onsite_paid_amount_change_requires_admin") {
        renderInlineMessage(root, "お支払い済みの現地払い予約で料金が変わる変更は、店舗へ直接ご相談ください。", "error");
        return;
      }
      renderInlineMessage(root, `変更申請に失敗しました(${reason})。`, "error");
    }
  });
}

export async function renderMyBookingsScreen(root: HTMLElement, ctx: AppContext): Promise<void> {
  root.innerHTML = '<p class="loading">予約情報を読み込み中…</p>';

  let data;
  try {
    data = await ctx.api.getMyBookings();
  } catch (error) {
    const reason = error instanceof ApiError ? error.reason ?? error.code : "unknown_error";
    root.innerHTML = `<p class="error">予約情報の取得に失敗しました(${escapeHtml(reason)})。時間をおいて再度お試しください。</p>`;
    return;
  }

  const bookingsHtml = data.bookings.length
    ? data.bookings.map(bookingCardHtml).join("")
    : '<p class="field-hint">現在、今後のご予約はありません。</p>';
  const refundsHtml = data.cancelled_refunds.length
    ? `<h3>返金済みのキャンセル予約</h3>${data.cancelled_refunds.map(cancelledRefundCardHtml).join("")}`
    : "";

  const usageHistory = data.usage_history ?? [];
  // 利用履歴セクション自体は0件でも常に表示する(2026-09-26追加指示)。
  // 「利用履歴」の存在自体をユーザーが把握できるようにするため、返金済み
  // キャンセル予約(refundsHtml)と違い0件時にセクションを消さない。
  // usage_historyの判定条件(reservation_status='利用完了')自体は変更しない。
  const usageHistoryHtml = `<h3>利用履歴（過去のご予約）</h3>${
    usageHistory.length > 0
      ? usageHistory.map(usageHistoryCardHtml).join("")
      : '<p class="field-hint">現在、利用履歴はありません。</p>'
  }`;
  root.innerHTML = `
    <h2>マイ予約</h2>
    <h3>現在・今後のご予約</h3>
    ${bookingsHtml}
    ${refundsHtml}
    ${usageHistoryHtml}
  `;

  root.querySelectorAll<HTMLButtonElement>('[data-action="change"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const booking = data.bookings.find((b) => b.booking_id === (btn.dataset.bookingId as string));
      if (booking) renderChangeRequestForm(ctx, booking, root);
    });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-action="change-arrival"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const booking = data.bookings.find((b) => b.booking_id === (btn.dataset.bookingId as string));
      if (booking) renderArrivalTimeForm(ctx, booking, root);
    });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-action="cancel"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const booking = data.bookings.find((b) => b.booking_id === (btn.dataset.bookingId as string));
      void handleCancel(ctx, btn.dataset.bookingId as string, root, booking);
    });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-action="refund-statement"]').forEach((btn) => {
    btn.addEventListener("click", () => downloadRefundStatement(ctx, btn.dataset.url as string, root));
  });
  root.querySelectorAll<HTMLButtonElement>('[data-action="receipt"]').forEach((btn) => {
    btn.addEventListener("click", () => showReceipt(ctx, btn.dataset.bookingId as string, root));
  });
  root.querySelectorAll<HTMLButtonElement>('[data-action="usage-history-hide"]').forEach((btn) => {
    btn.addEventListener("click", () => handleHideUsageHistory(ctx, btn.dataset.bookingId as string, root));
  });
  root.querySelectorAll<HTMLButtonElement>('[data-action="usage-history-detail"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const bookingId = btn.dataset.bookingId as string;
      const item = usageHistory.find((h) => h.booking_id === bookingId);
      if (item) renderUsageHistoryDetail(ctx, item, root);
    });
  });
}
